// @ts-nocheck
import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { HgProduct, ProductsQueryPayload } from "../../types/taxonomy";
import { PRODUCT_ID_QUERY } from "../../apis/taxonomy/queries";
import { isNilOrEmpty } from "../utils/dataUtils";
import { TAXONOMY_CLIENT } from "../../utils/consts";
import { useTaxonomyContext } from "../../contexts/Taxonomy/TaxonomyContext";

interface ProductByIds {
  loading: boolean;
  matchedProductsById: HgProduct[];
  categoryNameTree: string[];
  productsNameMapById: Record<number, string>;
}

/**
 * Fetches product data by array of ids
 * @example
 *  const { loading, matchedProductsById, categoryNameTree } = useProductById(ids);
 *
 * @returns { ProductByIds }
 *  Product by id data
 */

export const useMatchProductsById = (ids: number[]): ProductByIds => {
  const { productAttributes } = useTaxonomyContext();

  const { data, loading } = useQuery<ProductsQueryPayload, { ids: number[] }>(
    PRODUCT_ID_QUERY,
    {
      variables: {
        ids,
      },
      skip: isNilOrEmpty(ids),
      context: {
        clientName: TAXONOMY_CLIENT,
      },
    }
  );

  const matchedProductsById = useMemo(() => {
    if (!data?.product?.data || !productAttributes) return [];

    const level2AttributeIds = productAttributes.flatMap(
      (attr) =>
        attr.childrenAttributes
          ?.filter((child) => child.level === 2)
          .map((child) => child.id) || []
    );

    return data.product.data.map((product) => ({
      ...product,
      attributes: product.attributes?.filter((attr) =>
        level2AttributeIds.includes(attr.id)
      ),
    }));
  }, [data, productAttributes]);

  const categoryNameTree = useMemo(
    () =>
      (matchedProductsById && matchedProductsById[0]?.category?.nameTree) || [],
    [matchedProductsById]
  );

  const productsNameMapById = useMemo(() => {
    const map = {} as Record<number, string>;

    for (const { id, name } of matchedProductsById) {
      map[id] = name;
    }

    return map;
  }, [matchedProductsById]);

  return {
    loading,
    matchedProductsById,
    categoryNameTree,
    productsNameMapById,
  };
};
